/**
 * Sends an http visitor to the https version of the same URL.
 *
 * This is not only hygiene. `wss:` needs a secure context, so on http the SIP registration fails —
 * and it fails looking exactly like a firewall blocking the WebSocket, which is the one diagnosis
 * this page must not get wrong. Better to never run the test over http at all.
 */

export interface RedirectConditions {
	protocol: string
	/** The browser's own verdict: `http://localhost` is secure, a plain http host is not. */
	secureContext: boolean
	/** The dev server has no https to redirect to. */
	dev: boolean
}

export function shouldRedirectToHttps({ protocol, secureContext, dev }: RedirectConditions): boolean {
	if (dev || secureContext) return false
	return protocol === 'http:'
}

/**
 * Rewrites only the leading scheme. A naive replace would also corrupt an `http://` that appears
 * inside a query parameter.
 */
export function httpsUrlFor(href: string): string {
	return href.replace(/^http:\/\//i, 'https://')
}

/** Redirects if needed, replacing the history entry so Back does not bounce the visitor. */
export function enforceHttps(): void {
	const conditions = {
		protocol: location.protocol,
		secureContext: window.isSecureContext,
		dev: import.meta.env.DEV
	}

	if (shouldRedirectToHttps(conditions)) location.replace(httpsUrlFor(location.href))
}
